import { createRefinement, deleteRefinement } from '@kudos/http-client'
import { useApiError } from '@shared/errors/useApiError'
import { useMutation } from '@tanstack/react-query'
import { isEmpty } from 'lodash'
import { useRef, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { v4 as uuidv4 } from 'uuid'
import useRecognitionModal from '../useRecognitionModal'
import { RecognitionFormData } from './useSendRecognitionForm'

const useMessageRefinement = () => {
  const { dialogData } = useRecognitionModal()
  const [originalMessage, setOriginalMessage] = useState<string>('')
  const fallbackSessionIdRef = useRef<string | null>(null)

  const { control, setValue, setError } = useFormContext<RecognitionFormData>()
  const message = useWatch({ control, name: 'message' })
  const refinementId = useWatch({ control, name: 'refinementId' })

  const { handleApiError } = useApiError<RecognitionFormData>()

  const getSessionId = (): string => {
    const fromModal = dialogData?.refinementSessionId
    if (fromModal) return fromModal
    if (!fallbackSessionIdRef.current) {
      fallbackSessionIdRef.current = uuidv4()
    }
    return fallbackSessionIdRef.current
  }

  const { isPending: isPendingRefineMessage, mutateAsync: refineMessage } = useMutation({
    mutationFn: async () => {
      if (isEmpty(refinementId)) {
        setOriginalMessage(message)
      }

      return await createRefinement({
        message,
        sessionId: getSessionId(),
      })
    },
    onSuccess: data => {
      setValue('refinementId', data.refinementId, { shouldValidate: true })
      setValue('message', data.output, { shouldValidate: true })
    },
    onError: error => {
      handleApiError(error, setError, ['message'])
    },
  })

  const { isPending: isPendingUndoRefinedMessage, mutateAsync: deleteRefinementMessage } =
    useMutation({
      mutationFn: async () => {
        return await deleteRefinement(refinementId ?? '')
      },
      onSuccess: () => {
        setValue('refinementId', undefined, { shouldValidate: true })
        setValue('message', originalMessage, { shouldValidate: true })
      },
      onError: error => {
        handleApiError(error)
      },
    })

  return {
    isPendingRefineMessage,
    refineMessage,
    isPendingUndoRefinedMessage,
    deleteRefinementMessage,
    originalMessage,
    hasRefined: !!refinementId,
  }
}

export default useMessageRefinement
